<template>
  <CCard>
    <CCardBody>
      <h5 class="mb-3">{{ $t("sabbatical_adjustment.form_title") }}</h5>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <CForm @submit.prevent="handleSubmit(submit)">
          <CRow>
            <CCol md="6">
              <div class="form-field">
                <label>{{ $t("sabbatical_adjustment.employee") }} <span class="require">*</span></label>
                <ValidationProvider rules="required" v-slot="{ errors }" :name="$t('sabbatical_adjustment.employee')">
                  <Select2 class="select2-custom" v-model="form.employee_id" :options="employeeOptions" />
                  <span class="require">{{ errors[0] || apiErrors.employee_id }}</span>
                </ValidationProvider>
              </div>
              <div class="form-field">
                <label>{{ $t("sabbatical_adjustment.type") }} <span class="require">*</span></label>
                <b-form-radio-group v-model="form.type" :options="typeOptions" />
              </div>
              <CRow>
                <CCol sm="6" class="form-field">
                  <label>{{ $t("sabbatical_adjustment.days") }} <span class="require">*</span></label>
                  <ValidationProvider :rules="`required|min_value:0.01|max_value:${maxDays}`" v-slot="{ errors }" :name="$t('sabbatical_adjustment.days')">
                    <b-form-input v-model="form.days" type="number" step="0.01" min="0.01" :max="maxDays" />
                    <span class="require">{{ errors[0] || stepError || balanceError || apiErrors.days }}</span>
                  </ValidationProvider>
                </CCol>
                <CCol v-if="form.type === TYPE_ADD" sm="6" class="form-field">
                  <label>{{ $t("sabbatical_adjustment.month") }} <span class="require">*</span></label>
                  <ValidationProvider rules="required" v-slot="{ errors }" :name="$t('sabbatical_adjustment.month')">
                    <b-form-input v-model="form.month" type="month" :min="minMonth" :max="maxMonth" />
                    <span class="require">{{ errors[0] || apiErrors.month }}</span>
                  </ValidationProvider>
                </CCol>
              </CRow>
              <p v-if="form.type === TYPE_SUB" class="note">{{ $t("sabbatical_adjustment.sub_note") }}</p>
              <div class="form-field">
                <label>{{ $t("sabbatical_adjustment.reason") }} <span class="require">*</span></label>
                <ValidationProvider rules="required|max:255" v-slot="{ errors }" :name="$t('sabbatical_adjustment.reason')">
                  <b-form-textarea v-model="form.reason" rows="2" maxlength="255" />
                  <span class="require">{{ errors[0] || apiErrors.reason }}</span>
                </ValidationProvider>
              </div>
              <CButton type="submit" color="primary">{{ $t("sabbatical_adjustment.save") }}</CButton>
            </CCol>
            <CCol md="6">
              <label>{{ $t("sabbatical_adjustment.balance") }}</label>
              <div v-if="balance">
                <p class="balance-total">{{ $t("sabbatical_adjustment.balance_total", { total: balance.total }) }}</p>
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>{{ $t("sabbatical_adjustment.bucket_month") }}</th>
                      <th class="text-center">{{ $t("sabbatical_adjustment.days") }}</th>
                      <th>{{ $t("sabbatical_adjustment.expires_date") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="bucket in balance.buckets" :key="bucket.month">
                      <td>{{ formatMonth(bucket.month) }}</td>
                      <td class="text-center">{{ bucket.number_days_off }}</td>
                      <td>{{ formatDate(bucket.expires_date) }}</td>
                    </tr>
                    <tr v-if="!balance.buckets.length">
                      <td colspan="3" class="text-center">{{ $t("table.label_data_not_found") }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="note">{{ $t("sabbatical_adjustment.choose_employee") }}</p>
            </CCol>
          </CRow>
        </CForm>
      </ValidationObserver>
    </CCardBody>
  </CCard>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapActions } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import api from "../../api/api";

// Same bounds as backend SabbaticalAdjustmentRequest
const MAX_DAYS = 30;
const MONTH_RANGE = 12;
const TYPE_ADD = "add";
const TYPE_SUB = "sub";

const emptyForm = () => ({
  employee_id: "",
  type: TYPE_ADD,
  days: "",
  month: moment().format("YYYY-MM"),
  reason: ""
});

// Add / subtract days off of one employee (replacement of CLI `sabbatical:add`), emits "saved"
export default {
  name: "SabbaticalAdjustmentForm",
  // vee-validate components are not registered globally in this app
  components: { ValidationProvider, ValidationObserver },
  data() {
    return {
      TYPE_ADD,
      TYPE_SUB,
      maxDays: MAX_DAYS,
      minMonth: moment().subtract(MONTH_RANGE - 1, "months").format("YYYY-MM"),
      maxMonth: moment().format("YYYY-MM"),
      employees: [],
      balance: null,
      form: emptyForm(),
      apiErrors: {}
    };
  },
  computed: {
    typeOptions() {
      return [
        { value: TYPE_ADD, text: this.$t("sabbatical_adjustment.type_add") },
        { value: TYPE_SUB, text: this.$t("sabbatical_adjustment.type_sub") }
      ];
    },
    employeeOptions() {
      return this.employees.map(e => ({ id: e.id, text: `${e.id} - ${e.text}` }));
    },
    selectedEmployeeName() {
      const employee = this.employeeOptions.find(e => e.id === this.form.employee_id);
      return employee ? employee.text : "";
    },
    stepError() {
      // Any decimal allowed, max 2 digits (same as backend regex)
      const days = String(this.form.days);
      return days !== "" && !/^\d+(\.\d{1,2})?$/.test(days) ? this.$t("sabbatical_adjustment.step_error") : "";
    },
    // Subtract cannot exceed available balance (backend re-checks under lock)
    balanceError() {
      const days = parseFloat(this.form.days);
      return this.form.type === TYPE_SUB && this.balance && days > this.balance.total
        ? this.$t("sabbatical_adjustment.balance_error", { total: this.balance.total })
        : "";
    }
  },
  watch: {
    "form.employee_id"(employeeId) {
      this.balance = null;
      if (employeeId) this.fetchBalance(employeeId);
    },
    form: {
      deep: true,
      handler() {
        this.apiErrors = {};
      }
    }
  },
  created() {
    this.fetchEmployees();
  },
  methods: {
    ...mapActions("layout", ["setLoading"]),
    formatMonth(month) {
      return moment(month, "YYYY-MM").format("MM/YYYY");
    },
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    async fetchEmployees() {
      try {
        const response = await axios.get(api.ALL_EMPLOYEES);
        this.employees = response.data.employees || [];
      } catch (e) {
        // Error toast already shown by axios interceptor
      }
    },
    async fetchBalance(employeeId) {
      try {
        const response = await axios.get(`${api.SABBATICAL_ADJUSTMENTS}/balance/${employeeId}`);
        // Ignore late response of a previously selected employee
        if (employeeId === this.form.employee_id) this.balance = response.data;
      } catch (e) {
        // Error toast already shown by axios interceptor
      }
    },
    confirmAdjust() {
      const isAdd = this.form.type === TYPE_ADD;
      const message = this.$t(isAdd ? "sabbatical_adjustment.confirm_add" : "sabbatical_adjustment.confirm_sub", {
        days: this.form.days,
        employee: this.selectedEmployeeName,
        month: isAdd ? this.formatMonth(this.form.month) : "",
        reason: this.form.reason
      });
      // $confirm helper hides the cancel button, call bvModal directly to allow cancelling
      return this.$bvModal.msgBoxConfirm(message, {
        title: this.$t("sabbatical_adjustment.confirm_title"),
        okTitle: this.$t("sabbatical_adjustment.confirm_ok"),
        cancelTitle: this.$t("sabbatical_adjustment.confirm_cancel"),
        okVariant: isAdd ? "primary" : "danger",
        size: "sm",
        buttonSize: "sm",
        centered: true
      }).catch(() => false);
    },
    async submit() {
      if (this.stepError || this.balanceError) return;
      if (!(await this.confirmAdjust())) return;
      const payload = { ...this.form };
      if (payload.type === TYPE_SUB) delete payload.month;
      this.setLoading(true);
      try {
        const response = await axios.post(api.SABBATICAL_ADJUSTMENTS, payload);
        this.balance = response.data;
        this.form = { ...emptyForm(), employee_id: this.form.employee_id };
        this.$nextTick(() => this.$refs.observer.reset());
        this.$toast(this.$t("sabbatical_adjustment.save_success"), null, 2000, "b-toaster-top-center", "success");
        this.$emit("saved");
      } catch (error) {
        const messages = error.response && error.response.status === 422
          ? error.response.data.error.message : {};
        this.apiErrors = Object.keys(messages || {}).reduce(
          (acc, key) => ({ ...acc, [key]: messages[key][0] }), {}
        );
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.form-field {
  margin-bottom: 0.75rem;
}
label {
  font-weight: 600;
}
.require {
  color: red;
}
.note {
  color: #768192;
  font-style: italic;
}
.balance-total {
  font-weight: 600;
}
</style>
